import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const recaptchaVerifierRef = useRef(null);
  const recaptchaWidgetIdRef = useRef(null);
  const recaptchaContainerRef = useRef(null);

  const createRecaptchaContainer = () => {
    const container = document.createElement('div');
    container.id = `recaptcha-container-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
    container.style.position = 'fixed';
    container.style.left = '-9999px';
    container.style.top = '-9999px';
    container.style.width = '1px';
    container.style.height = '1px';
    container.style.overflow = 'hidden';
    document.body.appendChild(container);
    recaptchaContainerRef.current = container;
    return container;
  };

  const removeRecaptchaContainer = () => {
    const container = recaptchaContainerRef.current;
    if (container && container.parentNode) {
      container.parentNode.removeChild(container);
    }
    recaptchaContainerRef.current = null;
  };

  // Initialize reCAPTCHA
  const initializeRecaptcha = () => {
    if (!recaptchaVerifierRef.current) {
      try {
        removeRecaptchaContainer();
        const container = createRecaptchaContainer();

        const verifier = new RecaptchaVerifier(auth, container, {
          size: 'invisible',
          callback: (response) => {
            console.log('reCAPTCHA solved successfully');
          },
          'expired-callback': () => {
            console.log('reCAPTCHA expired, resetting');
            recaptchaVerifierRef.current = null;
          },
          'error-callback': (error) => {
            console.error('reCAPTCHA error:', error);
          }
        });
        recaptchaVerifierRef.current = verifier;
        recaptchaWidgetIdRef.current = null;
        return verifier;
      } catch (error) {
        console.error('Error initializing reCAPTCHA:', error);
        throw error;
      }
    }
    return recaptchaVerifierRef.current;
  };

  const clearRecaptcha = () => {
    const verifier = recaptchaVerifierRef.current;
    if (verifier) {
      try {
        verifier.clear();
      } catch (error) {
        // Avoid crashing if React strict mode or route changes trigger duplicate cleanup.
        console.warn('reCAPTCHA was already cleared:', error?.message || error);
      } finally {
        recaptchaVerifierRef.current = null;
        recaptchaWidgetIdRef.current = null;
      }
    }
    removeRecaptchaContainer();
  };

  // Sign up with email and password
  const signup = async (email, password, userData) => {
    try {
      const { user } = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update user profile
      await updateProfile(user, {
        displayName: userData.name
      });

      // Save user data to Firestore
      await setDoc(doc(db, 'users', user.uid), {
        name: userData.name,
        email: userData.email,
        phoneNumber: userData.phoneNumber || '',
        profileImageUrl: '',
        createdAt: Date.now(),
        fcmToken: '',
        ...userData
      });

      return user;
    } catch (error) {
      throw error;
    }
  };

  // Sign in with email and password
  const signin = async (email, password) => {
    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password);
      return user;
    } catch (error) {
      throw error;
    }
  };

  // Send OTP to phone number
  const sendOTP = async (phoneNumber) => {
    const shouldRetry = (errorCode) => [
      'auth/invalid-app-credential',
      'auth/missing-client-identifier',
      'auth/internal-error'
    ].includes(errorCode);

    const sendOtpOnce = async () => {
      // Always use a fresh verifier/token per OTP request to avoid stale app credentials.
      clearRecaptcha();
      const verifier = initializeRecaptcha();
      // Render once to ensure the verifier has a valid widget/client identifier.
      if (typeof verifier.render === 'function' && recaptchaWidgetIdRef.current == null) {
        recaptchaWidgetIdRef.current = await verifier.render();
      }
      return signInWithPhoneNumber(auth, phoneNumber, verifier);
    };

    try {
      console.log('Firebase Debug: Sending OTP to', phoneNumber);
      const confirmationResult = await sendOtpOnce();
      console.log('Firebase Debug: OTP sent successfully', confirmationResult);
      return confirmationResult;
    } catch (error) {
      console.error('Firebase Debug: Error sending OTP', error);
      console.error('Firebase Debug: Error code:', error.code);
      console.error('Firebase Debug: Error message:', error.message);

      if (shouldRetry(error?.code)) {
        try {
          console.log('Firebase Debug: Retrying OTP send with fresh reCAPTCHA verifier');
          clearRecaptcha();
          const retryResult = await sendOtpOnce();
          console.log('Firebase Debug: OTP sent successfully after retry', retryResult);
          return retryResult;
        } catch (retryError) {
          console.error('Firebase Debug: Retry failed', retryError);
          throw retryError;
        }
      }

      throw error;
    }
  };

  // Verify OTP and sign in
  const verifyOTP = async (confirmationResult, otp, userData = null) => {
    try {
      const { user } = await confirmationResult.confirm(otp);
      
      // If this is a new user (signup), save additional data
      if (userData) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (!userDoc.exists()) {
          await setDoc(doc(db, 'users', user.uid), {
            name: userData.name,
            email: userData.email || '',
            phoneNumber: user.phoneNumber,
            profileImageUrl: '',
            createdAt: Date.now(),
            fcmToken: '',
            ...userData
          });
        }
      }
      
      return user;
    } catch (error) {
      throw error;
    }
  };

  // Sign out
  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      throw error;
    }
  };

  // Get user data from Firestore
  const getUserData = async (uid) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        return userDoc.data();
      }
      return null;
    } catch (error) {
      console.error('Error getting user data:', error);
      return null;
    }
  };

  // Update user data in Firestore
  const updateUserData = async (uid, data) => {
    try {
      await setDoc(doc(db, 'users', uid), data, { merge: true });
    } catch (error) {
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return () => {
      unsubscribe();
      clearRecaptcha();
    };
  }, []);

  const value = {
    currentUser,
    signup,
    signin,
    logout,
    sendOTP,
    verifyOTP,
    getUserData,
    updateUserData,
    initializeRecaptcha,
    clearRecaptcha,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
